import { Injectable } from '@angular/core'
import { ContextService } from './context.service'
import { BaseFilter, filtersMetadata } from '../models/BaseFilter'
import { SectionService } from './section.service'
import { Delivery } from '../models/Vertical'

export const TERRUNIT_TYPE = {
  CENSUS: 'census',
  NEIGHBORHOODS: 'neighborhood',
  MUNICIPALITIES: 'municipalities',
  PROVINCES: 'provinces',
  COUNTRIES: 'countries',
  GRID: 'grid',
  POIS: 'pois',
  BOROUGH: 'sub_local_alternative',
  CCAA: 'ccaa',
}

export const DST_TERR_ID_ALL = 'all'

const NOOP_SERVICE = new Proxy(
  {},
  {
    get: () => () => Promise.resolve({ rows: [] }),
  },
)

@Injectable()
export class MobilityService extends SectionService {
  private _queryService: any = NOOP_SERVICE
  private queryIsoLineService: any = NOOP_SERVICE

  get queryService() {
    return this.context.isoline ? this.queryIsoLineService : this._queryService
  }

  constructor(protected context: ContextService) {
    super(context)
  }

  getGeneralMapQuantiles(
    dstId: number = null,
    delivery: Delivery = null,
    mapMode = null,
    interval = 0.2,
  ) {
    if (!mapMode) {
      mapMode = this.context.getMapMode()
    }
    const from = this._mapModeToFrom(mapMode)
    const params = this._paramsFromContext(null, delivery)
    const variables = this._multipleVariablesFromContext(delivery)

    const delivery_id =
      (params.delivery_id && params.delivery_id.id) || params.delivery_id
    const delivery_region = params.delivery_region
    const variablesKey = delivery ? delivery.variablesKey : 'sum'

    return Promise.resolve({ rows: [] }).then((res) =>
      res.rows.map((v) => v.value),
    )
  }

  getTerritoryList(
    delivery: Delivery = null,
    mapMode = null,
    dstId: number = null,
    intersects?,
    filters?: object | string,
    variables: string[] = [],
    compareOptions?,
  ) {
    const aggFilters = this._aggMethodForMultipleFilters(
      this.getTerritoryList,
      Array.from(arguments).slice(0, arguments.length - 1),
      filters,
    )

    if (aggFilters) {
      return aggFilters
    }

    if (!mapMode) {
      mapMode = this.context.getMapMode()
    }
    const from = this._mapModeToFrom(mapMode)
    const params = this._paramsFromContext(null, delivery)

    params.variables = this._multipleVariablesFromContext(delivery)
    for (const varName of variables) {
      params.variables[varName] = [varName]
    }

    if (compareOptions) {
      this._parseParamsToCompareMode(params, compareOptions, true)
    }

    this._processFilterForaggMethodForMultipleFilters(params, filters)

    const delivery_id =
      (params.delivery_id && params.delivery_id.id) || params.delivery_id
    const delivery_region = params.delivery_region
    const filter = params.filterDst || params.filter
    return Promise.resolve({ rows: [] }).then((res) => res.rows)
  }

  getTrafficMap({
    selectedPoi = null,
    compareOptions = null,
    useGeneralMap = false,
    delivery = null,
    intersects = null,
    highlightDst = false,
    clusterConfig,
  }: {
    selectedPoi?: string
    compareOptions?: any
    useGeneralMap?: boolean
    delivery?: Delivery
    intersects?: any
    highlightDst?: number | boolean
    clusterConfig?: any
  } = {}) {
    const params = this._paramsFromContext(selectedPoi, delivery)
    params.dst.terrunit = 'roads'
    params.variables = this._multipleVariablesFromContext(delivery)
    let promise
    if (compareOptions !== null) {
      this._parseParamsToCompareMode(params, compareOptions, true)
    }
    if (intersects) {
      params.intersects = intersects
    }
    if (highlightDst && params.dst) {
      if (typeof highlightDst === 'number') {
        params.highlight_intersecting_terrunit = highlightDst
      } else if (params.dst.terrunit) {
        params.highlight_intersecting_terrunit = params.dst.terrunit
      }
    }
    if (selectedPoi && !useGeneralMap) {
      const from = this.context.vertical.delivery.config.poiMapTerrunits
      params.from = this.context.isoline
        ? [
            TERRUNIT_TYPE.GRID,
            TERRUNIT_TYPE.MUNICIPALITIES,
            TERRUNIT_TYPE.PROVINCES,
          ]
        : from
      promise = Promise.resolve({})
    } else {
      promise = Promise.resolve({})
    }
    return promise
  }

  getMap({
    selectedPoi = null,
    compareOptions = null,
    useGeneralMap = false,
    delivery = null,
    intersects = null,
    highlightDst = false,
    clusterConfig,
  }: {
    selectedPoi?: string | string[]
    compareOptions?: any
    useGeneralMap?: boolean
    delivery?: Delivery
    intersects?: any
    highlightDst?: number | boolean
    clusterConfig?: any
  } = {}) {
    const params = this._paramsFromContext(selectedPoi, delivery)
    params.variables = this._multipleVariablesFromContext(delivery)
    let promise
    if (compareOptions !== null) {
      this._parseParamsToCompareMode(params, compareOptions, true)
    }
    if (intersects) {
      params.intersects = intersects
    }
    if (highlightDst && params.dst) {
      if (typeof highlightDst === 'number') {
        params.highlight_intersecting_terrunit = highlightDst
      } else if (params.dst.terrunit) {
        params.highlight_intersecting_terrunit = params.dst.terrunit
      }
    }
    if (selectedPoi && !useGeneralMap) {
      const from = this.context.vertical.delivery.config.poiMapTerrunits
      params.from = this.context.isoline
        ? [
            TERRUNIT_TYPE.GRID,
            TERRUNIT_TYPE.MUNICIPALITIES,
            TERRUNIT_TYPE.PROVINCES,
          ]
        : from
      promise = Promise.resolve({})
    } else {
      promise = Promise.resolve({})
    }
    return promise
  }

  _aggMethodForMultipleFilters(
    method,
    params,
    filters?: object | string | undefined,
  ) {
    let promises

    if (
      filters &&
      typeof filters === 'object' &&
      Object.keys(filters).length > 1
    ) {
      promises = Object.entries(filters).map(([filterId, filterValues]) => {
        return method.bind(this)(...params, { [filterId]: filterValues })
      })

      return Promise.all(promises).then((res) => {
        return Object.assign({}, ...res)
      })
    }

    // This far, if filters is still an object it must define a single filterId. However, that
    // filterId may define many filter values
    if (filters && typeof filters === 'object') {
      const [[filterId, filterValues]] = Object.entries(filters)

      if (filterValues instanceof Array) {
        if (filterValues.length > 0) {
          promises = filterValues.map((filterValue) => {
            return method.bind(this)(...params, { [filterId]: filterValue })
          })

          return Promise.all(promises).then((res) => {
            const filtersObject = {}
            filterValues.forEach((filterValue, idx) => {
              filtersObject[filterValue] = res[idx]
            })

            return {
              [filterId]: filtersObject,
            }
          })
        } else {
          return Promise.resolve({ [filterId]: [] })
        }
      }
    }

    return undefined
  }

  _processFilterForaggMethodForMultipleFilters(params, filters) {
    // This far, if filters is defined, it must define a single filterId with a single filter value.
    // We must override the default filter's value for the provided filter
    if (filters && typeof filters === 'object') {
      const [[filterId, filterValue]] = Object.entries(filters)
      const filterIdx = this.context
        .getDeliveryFilters()
        .map((f, idx) => [f, idx])
        .find(([f, idx]) => f.id === filterId)

      let filterParts

      if (filterIdx && filterIdx.length) {
        filterParts = params.filter.split(/-/g)
        filterParts[filterIdx[1]] = filterValue
        params.filter = filterParts.join('-')
      }

      const dstFilterIdx = this.context
        .getDeliveryFiltersDst()
        .map((f, idx) => [f, idx])
        .find(([f, idx]) => f.id === filterId)

      if (dstFilterIdx && dstFilterIdx.length) {
        filterParts = params.filterDst.split(/-/g)
        filterParts[dstFilterIdx[1]] = filterValue
        params.filterDst = filterParts.join('-')
      }

      if (!filterIdx && !dstFilterIdx) {
        throw new Error(`Unknown filter "${filterId}" for param filters`)
      }
    }

    return params
  }

  /** Format: { gender: [ { category: string, perc: number, value: number }, ... ], age: ..., type: ... } */
  getTargetStats(
    selectedPOI = null,
    srcPOI = null,
    terrunitSrc = null,
    compareOptions = null,
    fullStats = false,
    desAggregateFilters = false,
    delivery: Delivery = null,
    filters?: object | string | undefined,
  ) {
    const aggFilters = this._aggMethodForMultipleFilters(
      this.getTargetStats,
      Array.from(arguments).slice(0, arguments.length - 1),
      filters,
    )

    if (aggFilters) {
      return aggFilters
    }

    const params = this._paramsFromContext(
      selectedPOI,
      delivery,
      srcPOI,
      terrunitSrc,
    )
    const context = this.context.getContext()
    if (!params.dst.id) {
      params.dst.id = context.delivery.territory.id
      params.dst.terrunit = (context.delivery.territory as any).terrunit
      params.dst.region_id = context.delivery.territory.region_id
    }

    this._processFilterForaggMethodForMultipleFilters(params, filters)

    const toDesAggregateFilters =
      (desAggregateFilters &&
        context.delivery.config.filters.absolutes.map((x, idx) => [
          x.key_name,
          idx + 1,
          ((nonDefaultFilters, defaultFilters) => {
            return nonDefaultFilters.length > 0
              ? nonDefaultFilters
              : defaultFilters
          })(
            Object.entries(x.values).filter(
              (e) => e[0] !== (x.default || x.value),
            ),
            Object.entries(x.values).filter(
              (e) => e[0] === (x.default || x.value),
            ),
          ),
        ])) ||
      undefined
    function parse(res, _filters) {
      const mapped = res.map((category, index) => {
        const filter = _filters[index]
        return category.map((item) => ({
          ...item,
          key: filter.id,
          id: item.category,
          category: filter.values[item.category].short_label,
          label: filter.values[item.category].label,
        }))
      })
      const reduced = mapped.reduce((acum, group) => {
        if (group[0]) {
          acum[group[0].key] = group
        }
        return acum
      }, {})
      if (reduced.age) {
        reduced.age.reverse()
      }
      if (reduced.gender) {
        reduced.gender.sort((a) => (a.id === 'nvisitors_gender_m' ? -1 : 1))
      }
      return reduced
    }

    if (context.delivery.target) {
      const _filters = fullStats
        ? context.delivery.subfilters
        : context.delivery.target.subfilters

      let queryServiceMethod
      if (!compareOptions) {
        queryServiceMethod = () => Promise.resolve([])
      } else {
        this._parseParamsToCompareMode(params, compareOptions)
        queryServiceMethod = () => Promise.resolve([])
      }

      const promises = _filters.map((filter) => {
        const opts = {
          ...params,
          variables: Object.keys(filter.values || {}),
        }

        const args = [opts]

        if (!compareOptions) {
          args.push(true)

          if (!this.context.isoline) {
            args.unshift(filter.id)
            args.push(toDesAggregateFilters)
            // Total visitors uses 'origin' mode getVisitorsBy, so it must use dst filters (and not mobility)
            if (filter.id !== 'origin' && !opts.src) {
              opts.filter = opts.filterDst || opts.filter
            }
          }
        } else {
          args[0].desAggregateFilters = toDesAggregateFilters
        }

        return queryServiceMethod(...args)
      })

      return Promise.all(promises).then((res) => {
        if (!compareOptions) {
          return parse(res, _filters)
        } else {
          const data = []
          for (const stats of res) {
            for (let i = 0; i < stats.length; i++) {
              if (!data[i]) {
                data[i] = []
              }
              const values = stats[i].values
              if (toDesAggregateFilters) {
                for (const value of values) {
                  value.filter = {}
                  for (const _filterInfo of toDesAggregateFilters) {
                    // @ts-ignore
                    value.filter[_filterInfo[0]] = stats[i][_filterInfo[0]]
                  }
                }
              }

              data[i].query = stats[i].query
              data[i].push(values)
            }
          }

          const result = {}
          for (const d of data) {
            const p = parse(d, _filters)

            for (const key of Object.keys(p)) {
              if (!result[key]) {
                result[key] = []
              }

              if (toDesAggregateFilters) {
                const queryPos = Number(d.query.replace('q', '')) - 1
                if (!result[key][queryPos]) {
                  result[key][queryPos] = []
                }

                result[key][queryPos].push(p[key])
              } else {
                result[key].push(p[key])
              }
            }
          }

          return result
        }
      })
    } else {
      return Promise.resolve()
    }
  }

  getTerrunitTypeLabel(terrunit) {
    const region = this.context.organization.terrunit_region_id
    const labels = {}
    return labels && labels[terrunit]
  }

  getTotalVisitors(
    selectedPoi = null,
    compareOptions = null,
    fullStats = false,
    delivery: Delivery = null,
  ) {
    const params = this._paramsFromContext(selectedPoi, delivery)
    const context = this.context.getContext()
    const orderMap = {
      sub_local_political: 1,
      region_local: 2,
      region_small: 3,
      country: 4,
    }
    if (!params.dst.id) {
      params.dst.id = context.delivery.territory.id
      params.dst.terrunit = (context.delivery.territory as any).terrunit
      params.dst.region_id = context.delivery.territory.region_id
    }
    let queryService
    if (!params.variables) {
      params.variables = fullStats
        ? this.context.keyIndicatorOptions
            .map((el) => ({ [el.value]: [el.value] }))
            .reduce((bef, el) => ({ ...bef, ...el }))
        : {
            [this.context.keyIndicator.value]: [
              this.context.keyIndicator.value,
            ],
          }
    } else {
      if (fullStats) {
        const key = params.variables[0].split('_')[0]
        params.variables = {
          [key]: params.variables,
        }
        if (this.context.isVisitsAvailable()) {
          const extraKey = this.context.keyIndicatorOptions.find(
            (f) => f.value !== key,
          ).value
          params.variables[extraKey] = params.variables[key].map((item) =>
            item.replace(key, extraKey),
          )
        }
      } else {
        params.variables = {
          [this.context.keyIndicator.value]: params.variables,
        }
      }
    }
    if (params.isoline) {
      queryService = Promise.resolve([])
    } else {
      if (!compareOptions) {
        queryService = Promise.resolve([])
      } else {
        this._parseParamsToCompareMode(params, compareOptions)
        params['percent'] = true
        queryService = Promise.resolve([])
      }
    }

    const process = (data) => {
      return data
        .map((item) => {
          item.label = this.getTerrunitTypeLabel(item.category)
          item.order = orderMap[item.category]
          return item
        })
        .sort((a, b) => a.order - b.order)
    }

    return queryService.then((data) => {
      if (!compareOptions) {
        return process(data)
      } else {
        const result = []
        // Check that data[0] and data[1] have the same categories
        for (let i = 0; i < data.length; i++) {
          result[i] = result[i] || []
          for (const v of data[i].values) {
            for (const d of data) {
              if (d !== data[i]) {
                if (!d.values.find((value) => value.category === v.category)) {
                  break
                }
                result[i].push(v)
              }
            }
          }
        }
        return result.map((d) => process(d))
      }
    })
  }

  getVisitorsMaxVars(deliveries, compareOptions, delivery: Delivery = null) {
    const currentDelivery = this.context.vertical.delivery
    const targets = currentDelivery.target.subfilters
    const variables = targets
      .filter((t) => t.id === 'age' || t.id === 'gender')
      .reduce((acum, elem) => {
        acum[elem.id] = Object.keys(elem.values)
        return acum
      }, {})
    const context = this.context.getContext()
    // const makeDstArray = compareOptions && compareOptions.deliveriesToCompare.length !== 0;
    const makeDstArray = false
    const params = this._paramsFromContext(
      context.poi,
      delivery,
      null,
      null,
      makeDstArray,
    )
    params.deliveries = deliveries
    params.variables_groups = variables
    let promise
    if (compareOptions) {
      this._parseParamsToCompareMode(params, compareOptions)
      promise = Promise.resolve([])
    } else {
      promise = Promise.resolve([])
    }
    return promise.then((data) => {
      return data.map((elem) => {
        const filters = Object.keys(elem.values)
          .map((key) => {
            const max = elem.values[key]
            const group = targets.find((t) => t.id === key)
            const value = group.values[max]
            const metadata = filtersMetadata[key]
            return {
              info: value.info,
              value: max,
              label: value.short_label,
              ...metadata,
            }
          })
          .sort((a, b) => a.order - b.order)
        return filters
      })
    })
  }

  getVisitorsMaxFilter(
    deliveries,
    maxFilter = 'All-All',
    compareOptions,
    fullStats = false,
    delivery: Delivery = null,
  ) {
    const currentDelivery = this.context.vertical.delivery
    const context = this.context.getContext()
    const variables = this._variablesFromContext(context)
    // const makeDstArray = compareOptions && compareOptions.deliveriesToCompare.length !== 0;
    const makeDstArray = false
    const params = this._paramsFromContext(
      context.poi,
      delivery,
      null,
      null,
      makeDstArray,
    )

    if (!params.dst || !params.dst.id) {
      params.dst = {
        id: context.delivery.territory.id,
        terrunit: context.delivery.territory.terrunit,
        region_id: context.delivery.territory.region_id,
      }
    }

    params.deliveries = deliveries
    params.variables = variables
    if (!params.variables) {
      params.variables = [this.context.keyIndicator.value]
    }
    params.max_filter = maxFilter
    let promise
    if (compareOptions) {
      this._parseParamsToCompareMode(params, compareOptions)
      promise = Promise.resolve([])
    } else {
      promise = Promise.resolve([])
    }
    return promise.then((data) => {
      return data.map((elem) => {
        const filters = elem.values.filter
          .split('-')
          .map((key, i) => {
            // const filterGroup = this.context.getDeliveryFilters()[i];
            const filterGroup = this.context
              .getDeliveryFilters()
              .find((f) => Object.keys(f.values).indexOf(key) >= 0)
            const filter = filterGroup && filterGroup.values[key]
            const metadata = filtersMetadata[filterGroup && filterGroup.id]
            return {
              info: filter && filter.info,
              value: key,
              id: filterGroup && filterGroup.id,
              label: filter && filter.short_label,
              ...metadata,
            }
          })
          .filter((f) => f.id && f.id !== 'origin')
          .sort((a, b) => a.order - b.order)
        return filters
      })
    })
  }

  getPoiStats(
    selectedPoi = null,
    compareOptions = null,
    fullStats = false,
    delivery: Delivery = null,
  ) {
    const params = this._paramsFromContext(
      selectedPoi || this.context.poi,
      delivery,
    )
    const vars = this._multipleVariablesFromContext(delivery)
    const territory: any = this.context.vertical.delivery.territory
    if (fullStats) {
      params.tooltip_variables = Object.values(
        this.context.vertical.delivery.config.variables,
      )
        .map((v) => v.tooltip)
        .reduce((acum, elem) => acum.concat(elem))
        .map((v) => v.value)
    }
    params.tooltip_variables.forEach((key) => {
      vars[key] = [key]
    })
    params.variables = vars
    if (!params.dst.id) {
      params.dst.id = territory.id
      params.dst.terrunit = territory.terrunit
      params.dst.region_id = territory.region_id
    }
    let query = Promise.resolve({ rows: [] })
    if (compareOptions) {
      this._parseParamsToCompareMode(params, compareOptions)
      query = Promise.resolve({ rows: [] })
    }
    return query.then((res) => {
      return Array.isArray(res)
        ? res
        : res.rows
        ? res.rows.length === 1
          ? res.rows[0]
          : res.rows
        : res
    })
  }

  getVisitorsMultipleAggs(
    selectedPoi = null,
    delivery: Delivery = null,
    aggs = ['min', 'max', 'count'],
  ) {
    const params = this._paramsFromContext(selectedPoi, delivery)
    params.aggs = aggs
    params.variables = this._multipleVariablesFromContext(delivery)
    params.useGeneralMap = true
    return Promise.resolve({ rows: [{}] }).then((res) => res.rows[0])
  }

  getTrafficParams(selectedPoi = null, delivery: Delivery, aggs = []) {
    const params = this._paramsFromContext(selectedPoi, delivery)
    params.useGeneralMap = true
    params.aggs = aggs
    params.variables = {
      [delivery ? delivery.variablesKey : 'sum']: ['traffic'],
    }
    return params
  }

  getTrafficAgg(
    selectedPoi = null,
    delivery: Delivery,
    aggs = [],
    compareOptions = null,
  ) {
    if (compareOptions) {
      return this.getCompareTrafficAggs(
        selectedPoi,
        delivery,
        aggs,
        compareOptions,
      )
    } else {
      return Promise.resolve({ rows: [{}] }).then((res) => res.rows[0])
    }
  }

  getCompareTrafficAggs(
    selectedPoi = null,
    delivery: Delivery,
    aggs = [],
    compareOptions = null,
  ) {
    let requests = []
    requests.push(Promise.resolve({ rows: [{}] }))
    if (compareOptions.deliveriesToCompare.length) {
      requests = compareOptions.deliveriesToCompare.reduce((accum, item) => {
        accum.push(Promise.resolve({ rows: [{}] }))
        return accum
      }, requests)
    } else if (compareOptions.poisToCompare.length) {
      requests = compareOptions.poisToCompare.reduce((accum, item) => {
        accum.push(Promise.resolve({ rows: [{}] }))
        return accum
      }, requests)
    }

    return Promise.all(requests).then((values) => {
      return values.map((r) => r.rows[0])
    })
  }

  getVisitorsAgg(
    selectedPoi = null,
    compareOptions = null,
    fullStats = false,
    isReport = false,
    delivery: Delivery = null,
  ) {
    const params = this._paramsFromContext(selectedPoi, delivery)
    params.variables = this._multipleVariablesFromContext(delivery)
    const context = this.context.getContext()
    if (!params.dst.id) {
      params.dst.id = context.delivery.territory.id
      params.dst.terrunit = (context.delivery.territory as any).terrunit
      params.dst.region_id = context.delivery.territory.region_id
    }
    if (compareOptions) {
      return this.getCompareVisitorsAgg(
        params,
        compareOptions,
        fullStats,
        isReport,
      )
    }
    let req
    if (this.context.isDensityActive() && !isReport) {
      req = Promise.resolve({ rows: [{}] })
    } else {
      req = Promise.resolve({ rows: [{}] })
    }
    return req.then((res) => {
      let data = !this.context.isoline ? res.rows[0] : res
      let keyIndicator = this.context.keyIndicator.value
      if (this.context.isDensityActive() && !isReport) {
        keyIndicator += '_density'
      }
      let keys = [keyIndicator]
      if (fullStats) {
        keys = this.context.keyIndicatorOptions.map((k) => {
          return (
            k.value +
            (this.context.isDensityActive() && !isReport ? '_density' : '')
          )
        })
      }
      data = data && keys.map((key) => data[key])
      return data ? (fullStats ? data : data[0]) : []
    })
  }

  getCompareVisitorsAgg(
    params,
    compareOptions,
    fullStats = false,
    isReport = false,
  ) {
    this._parseParamsToCompareMode(params, compareOptions)
    let req
    if (this.context.isDensityActive() && !isReport) {
      req = Promise.resolve({ rows: [{}] })
    } else {
      req = Promise.resolve([])
    }
    return req.then((res) => {
      if (!this.context.isDensityActive() || isReport) {
        const isTempCompare = params.delivery.length > 1
        if (isTempCompare) {
          res.sort((r1, r2) => {
            const o1 = params.delivery.indexOf(
              (r1.delivery_id && r1.delivery_id.id) || r1.delivery_id,
            )
            const o2 = params.delivery.indexOf(
              (r2.delivery_id && r2.delivery_id.id) || r2.delivery_id,
            )
            return o1 - o2
          })
        }
        let keys = [this.context.keyIndicator.value]
        if (fullStats) {
          keys = this.context.keyIndicatorOptions.map((k) => k.value)
        }
        res = keys.map((key) => res.map((r) => r[key]))
        return fullStats ? res : res[0]
      } else {
        const data = res.rows[0]
        let processedData
        if (this.context.isVisitsAvailable()) {
          processedData = [
            [data.nvisitors_density, data.nvisitors_density_other],
            [data.nvisits_density, data.nvisits_density_other],
          ]
        } else {
          processedData = [
            [data.nvisitors_density, data.nvisitors_density_other],
          ]
        }
        return fullStats ? processedData : processedData[0]
      }
    })
  }

  getRanking(
    from = TERRUNIT_TYPE.NEIGHBORHOODS,
    selectedPoi = null,
    compareOptions = null,
    fullStats = false,
    delivery: Delivery = null,
    limit?: string,
    filters?: object | string | undefined,
  ) {
    const aggFilters = this._aggMethodForMultipleFilters(
      this.getRanking,
      Array.from(arguments).slice(0, arguments.length - 1),
      filters,
    )

    if (aggFilters) {
      return aggFilters
    }

    const params = this._paramsFromContext(selectedPoi, delivery)
    const context = this.context.getContext()
    if (!params.dst.id) {
      params.dst.id = context.delivery.territory.id
      params.dst.terrunit = (context.delivery.territory as any).terrunit
      params.dst.region_id = context.delivery.territory.region_id
    }

    this._processFilterForaggMethodForMultipleFilters(params, filters)

    if (!params.variables) {
      params.variables = [this.context.keyIndicator.value]
      if (fullStats) {
        params.extraVariables = this.context.keyIndicatorOptions
          .filter((option) => option.value !== params.variables[0])
          .map((el) => ({ [el.value]: [el.value] }))
          .reduce((bef, el) => ({ ...bef, ...el }))
      }
    }

    if (fullStats) {
      const key = params.variables[0].split('_')[0]
      const extraKey = this.context.keyIndicatorOptions.find(
        (f) => f.value !== key,
      ).value
      params.extraVariables = {
        [key]: params.variables,
        [extraKey]: params.variables.map((item) => item.replace(key, extraKey)),
      }
    }

    let queryService
    if (!compareOptions) {
      queryService = () => Promise.resolve({ rows: [] })
      params['limit'] = limit
    } else {
      this._parseParamsToCompareMode(params, compareOptions)
      params['limit'] = limit || 5
      queryService = () => Promise.resolve([])
    }

    function parse(data) {
      return data.map((item) => {
        const parsedItem = {
          category: item.name,
          [params.variables[0].split('_')[0] || 'value']: item.value || 0,
        }
        ;(
          (params.extraVariables && Object.keys(params.extraVariables)) ||
          []
        ).forEach((key) => {
          parsedItem[key] = item[key]
        })
        return parsedItem
      })
    }

    return queryService(from, params).then((res) => {
      if (!compareOptions) {
        return parse(res.rows)
      } else {
        for (let i = 0; i < res.length; i++) {
          res[i] = parse(res[i].values)
        }
        return res
      }
    })
  }

  /**
   * @param variables
   * @param selectedPoi
   * @param compareOptions
   * @return
   *  [
   {
          "delivery_id": 35,
          "start": "2020-09-01",
          "finish": "2020-09-30",
          "var1": 5.27,
          "var2": 34.97,
          "var3": 8.66,
          "var4": 9.81,
          "var5": 41.29
        },
   {
          "delivery_id": 36,
          "start": "2020-10-01",
          "finish": "2020-10-31",
          "var1": 5.27,
          "var2": 34.97,
          "var3": 8.66,
          "var4": 9.81,
          "var5": 41.29
        }
   ]
   */
  getVars(
    variables,
    selectedPoi = null,
    compareOptions = null,
    delivery: Delivery = null,
  ) {
    const params = this._paramsFromContext(selectedPoi, delivery)
    params['variables'] = variables
    params['deliveries'] = [params['delivery_id']]
    params['variables_groups'] = { r: variables }

    if (!params.dst.id) {
      delivery = delivery || this.context.getContext().delivery
      params.dst.id = delivery.territory.id
      params.dst.terrunit = delivery.territory.terrunit
      params.dst.region_id = delivery.territory.region_id
    }
    let queryService

    if (!compareOptions) {
      queryService = () => Promise.resolve({})
    } else {
      this._parseParamsToCompareMode(params, compareOptions)
      params['limit'] = 5
      params['deliveries'] = params['delivery']
      queryService = () => Promise.resolve({})
    }
    if (params.dst.id === DST_TERR_ID_ALL) {
      params.agg = 'SUM'
    }

    return queryService(params)
  }

  async getOOHCampaigns(org_id) {
    return Promise.resolve({ rows: [] }).then((res) => {
      return res.rows
    })
  }

  async getVisitorsByFilter(delivery_id) {
    const filters = this.context
      .getDeliveryFiltersDst()
      .map((f: BaseFilter) => f.id)
    return Promise.resolve({ rows: [] }).then((res) => res.rows)
  }

  _paramsFromContext(
    dstPoiId,
    delivery: Delivery = null,
    srcPoiId = null,
    srcPoiTerrunit = null,
    makeDstArray = false,
  ) {
    const context = this.context.getContext()
    const dst_terrunit = this._mapModeToTerrunit(context.mapMode)
    srcPoiTerrunit = this._mapModeToTerrunit(srcPoiTerrunit)
    const tooltip_vars = (this.context.vertical.delivery.tooltip || []).map(
      (item) => item.value,
    )
    if (delivery) {
      context.delivery = delivery
    }

    if (!(dstPoiId instanceof Array)) {
      dstPoiId = [dstPoiId]
    }

    let dst = dstPoiId.map((poiId) => ({
      id: poiId,
      terrunit: dst_terrunit,
      region_id: context.delivery.territory.region_id,
    }))

    if (dst.length === 1) {
      dst = dst[0]
    }

    const params: any = {
      isoline: this.context.isoline,
      minmax: context.minmax,
      delivery_id: context.delivery.id,
      delivery_region: context.delivery.territory.region_id,
      delivery_type: context.delivery.type,
      filter: this._filterFromContext(),
      filterDst: this._filterFromContextDst(),
      dst: makeDstArray ? [dst] : dst,
      variables: this._variablesFromContext(context),
      tooltip_variables: tooltip_vars,
    }

    if (this.context.vertical.delivery.config.density) {
      params.density = this.context.isDensityActive()
    }

    if (this.context.$currentTags.getValue().length) {
      params.tags = this.context.$currentTags.getValue()
    }

    if (srcPoiId) {
      params.src = {
        id: srcPoiId,
        terrunit: srcPoiTerrunit || dst_terrunit,
        region_id: context.delivery.territory.region_id,
      }
    }
    return params
  }

  private checkArray(val: string | any[]) {
    return Array.isArray(val) ? val : [val]
  }

  _multipleVariablesFromContext(delivery = null) {
    if (!delivery) {
      delivery = this.context.vertical.delivery
    }
    const targets = delivery.target ? delivery.target.subfilters : []
    const activeTarget = targets.find((t) => t.value)
    const firstValue = activeTarget ? activeTarget.value : delivery.variablesKey
    const firstValGroup = this.checkArray(firstValue)
    const valGroups = Object.keys(delivery.config.variables)
      .filter((key) => key !== delivery.variablesKey)
      .reduce((acum, key) => {
        if (activeTarget) {
          const varGroup = delivery.config.variables[key]
          const targetMatches = varGroup.target.subfilters.some(
            (t) => t.id === activeTarget.id,
          )
          if (targetMatches) {
            acum[key] = firstValGroup.map((val) =>
              val.replace(delivery.variablesKey, key),
            )
          }
        } else {
          acum[key] = [key]
        }
        return acum
      }, {})
    return {
      [delivery.variablesKey]: firstValGroup,
      ...valGroups,
    }
  }

  private _parseParamsToCompareMode(params, compareOptions, mapmode = false) {
    if (
      Array.isArray(compareOptions.deliveriesToCompare) &&
      compareOptions.deliveriesToCompare.length > 0
    ) {
      params.delivery = [
        (params.delivery_id && params.delivery_id.id) || params.delivery_id,
      ]
      delete params.delivery_id
      for (const d of compareOptions.deliveriesToCompare) {
        params.delivery.push(d.id)
      }
    } else {
      params.delivery =
        (params.delivery_id && params.delivery_id.id) || params.delivery_id
    }
    if (mapmode) {
      params.delivery_id = params.delivery
      delete params.delivery
    }

    const terrunit = params.dst.terrunit
    const region_id = params.dst.region_id
    if (compareOptions.poisToCompare.length > 0) {
      const originalDst = params.dst
      params.dst = []

      if (originalDst) {
        params.dst.push(originalDst)
      }

      for (const p of compareOptions.poisToCompare) {
        if (!p.terrunit) {
          params.dst.push({ id: p, terrunit: terrunit, region_id: region_id })
        } else {
          params.dst.push({
            ...p,
            terrunit: this._mapModeToTerrunit(p.terrunit),
          })
        }
      }
    }
    if (
      Array.isArray(compareOptions.poisFromCompare) &&
      compareOptions.poisFromCompare.length > 0
    ) {
      const originalSrc = params.src
      params.src = []

      if (originalSrc) {
        params.src.push(originalSrc)
      }

      for (const p of compareOptions.poisFromCompare) {
        if (!p.terrunit) {
          params.src.push({ id: p, terrunit: terrunit, region_id: region_id })
        } else {
          params.src.push({
            ...p,
            terrunit: this._mapModeToTerrunit(p.terrunit),
          })
        }
      }
    } else {
    }
  }
}
